/**
 * POST to a GenBI endpoint that streams its response.
 *
 * /create-template and /update-template emit newline-delimited JSON: an "accepted"
 * heartbeat every 5s while the save runs, then the usual status envelope as the last
 * line. The heartbeats only exist to keep the connection alive past the API Gateway
 * idle timeout — they carry no result.
 *
 * axios buffers the whole body and hands back one blob of text, so result.status reads
 * undefined. This reads the stream instead, drops the heartbeats and returns
 * { data: <last line> } — the same shape axios.post returns, so call sites are unchanged.
 *
 * Not for /doc-import/heading-mapping: that one splits a large result across
 * result_chunk lines, which ImportFromDocumentModal reassembles itself.
 */

const HEARTBEAT_STATUSES = ['accepted', 'running'];

// Returns the parsed envelope, or null for a heartbeat / blank line.
const parseStreamLine = (line) => {
  if (!line) return null;
  const parsed = JSON.parse(line);
  return HEARTBEAT_STATUSES.includes(parsed.status) ? null : parsed;
};

export const postNdjson = async (url, payload, headers) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      Authorization: headers.Authorization || headers.authorization,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  if (!response.body) throw new Error('Streamed response is unavailable.');

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResult = null;

  // The last line normally ends with \n, but tolerate a stream that stops without one.
  // A trailing fragment that will not parse is only fatal if no result arrived.
  const flushRemainder = () => {
    const remainder = buffer.trim();
    buffer = '';
    if (!remainder) return;
    try {
      const parsed = parseStreamLine(remainder);
      if (parsed) finalResult = parsed;
    } catch (parseErr) {
      if (!finalResult) throw parseErr;
      console.warn('[postNdjson] ignoring unparsable trailing chunk:', remainder);
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);
        const parsed = parseStreamLine(line);
        if (parsed) finalResult = parsed;
      }
    }
  } catch (streamErr) {
    // The save may already have completed — the connection dropping on the way out
    // must not be reported as a failed save. Same tolerance as ImportFromDocumentModal.
    flushRemainder();
    if (!finalResult) throw streamErr;
    console.warn('[postNdjson] stream ended with a transport error after the result:', streamErr);
  }

  flushRemainder();

  if (!finalResult) throw new Error('The connection closed before the result arrived.');

  return { data: finalResult };
};

export default postNdjson;
